import {
  QUANTITY_NUMBER_FORMAT,
  TOTAL_NUMBER_FORMAT,
  UNITCOST_NUMBER_FORMAT,
  applyGridStyle,
  applyRangeFormats,
  rangeFormatting,
} from "../../styles/worksheet";
import { safeWorksheetName } from "../../workbook";
import { generateGCGRLineReport, generateGcStaffReport } from "../gcgr";
import { dateStampSheet } from "../helpers/dynamicSheets";
import { rowNames } from "../helpers/rangeNames";
import { titleMaker } from "./common";

export const createDefaultStaffDetailTemplate = async () => {
  try {
    return Excel.run(async (context) => {
      const workbook = context.workbook;

      //Staff Detail
      const safeName = await safeWorksheetName(workbook, "GCGR Staff Detail");
      const sheet = workbook.worksheets.add(safeName);
      await context.sync();

      await titleMaker(sheet, "GCGR Staff Detail", "gcgr");
      await dateStampSheet(sheet);

      const rowHeights = [15, 30, 25, 25, 25];
      rowHeights.forEach((height, index) => {
        sheet.getRange(`${index + 5}:${index + 5}`).format.rowHeight = height;
      });

      const columnWidths = [20, 200, 200, 70, 70, 70, 70, 70, 70, 150];
      columnWidths.forEach((width, index) => {
        sheet.getRange(`${String.fromCharCode(65 + index)}:${String.fromCharCode(65 + index)}`).format.columnWidth =
          width;
      });

      sheet.getRange("1:15").format.verticalAlignment = Excel.VerticalAlignment.center;
      await context.sync();

      const staffHeaders: rangeFormatting[] = [
        { rangeAddress: "B6", value: "Name", name: "\\position_name", Halignment: "Left" },
        { rangeAddress: "C6", value: "Employee", name: "\\employee_name", Halignment: "Left" },
        { rangeAddress: "D6", value: "Start", name: "\\start_date", Halignment: "Left" },
        { rangeAddress: "E6", value: "Finish", name: "\\finish_date", Halignment: "Left" },
        { rangeAddress: "F6", value: "% of Time", name: "\\allocation", Halignment: "Left" },
        { rangeAddress: "G6", value: "Duration", name: "\\duration", Halignment: "Center" },
        { rangeAddress: "H6", value: "UOM", name: "\\uom", Halignment: "Center" },
        { rangeAddress: "I6", value: "Rate", name: "\\rate", Halignment: "Center" },
        { rangeAddress: "J6", value: "Total", name: "\\grand_total", Halignment: "Right" },
      ];
      staffHeaders.forEach((header) => {
        header.fontSize = 12;
        header.bold = true;
      });
      applyRangeFormats(sheet, staffHeaders);
      applyGridStyle({ range: sheet.getRange("B6:J6"), borderColor: "Black", weight: "Medium", edges: ["EdgeBottom"] });

      const staffDataRows: rangeFormatting[] = [
        { rangeAddress: "B7", name: rowNames.gcgrStaffItems },
        { rangeAddress: "D7", numberFormat: "mm/dd/yyyy", Halignment: "Left" },
        { rangeAddress: "E7", numberFormat: "mm/dd/yyyy", Halignment: "Left" },
        { rangeAddress: "F7", numberFormat: "0%;(0)%;-", Halignment: "Right" },
        { rangeAddress: "G7", numberFormat: QUANTITY_NUMBER_FORMAT, Halignment: "Right" },
        { rangeAddress: "H7", Halignment: "Left" },
        { rangeAddress: "I7", numberFormat: UNITCOST_NUMBER_FORMAT, Halignment: "Right" },
        { rangeAddress: "J7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
      ];
      applyRangeFormats(sheet, staffDataRows);
      await context.sync();
      await generateGcStaffReport(sheet);
      sheet.activate();
      await context.sync();
    });
  } catch (error) {
    throw "Error creating default GCGR template: " + error;
  }
};

export const createDefaultGCGRTemplate = async () => {
  try {
    return Excel.run(async (context) => {
      const workbook = context.workbook;
      const safeName = await safeWorksheetName(workbook, "GCGR Estimate");
      const sheet = workbook.worksheets.add(safeName);
      await context.sync();

      await titleMaker(sheet, "GCGR Estimate", "gcgr");
      await dateStampSheet(sheet);

      const rowHeights = [15, 30, 25, 25, 25];
      rowHeights.forEach((height, index) => {
        sheet.getRange(`${index + 5}:${index + 5}`).format.rowHeight = height;
      });

      const columnWidths = [20, 400, 70, 70, 90, 150];
      columnWidths.forEach((width, index) => {
        sheet.getRange(`${String.fromCharCode(65 + index)}:${String.fromCharCode(65 + index)}`).format.columnWidth =
          width;
      });

      sheet.getRange("1:15").format.verticalAlignment = Excel.VerticalAlignment.center;
      await context.sync();

      const lineItemHeaders: rangeFormatting[] = [
        { rangeAddress: "B6", value: "Name", name: "\\name", Halignment: "Left" },
        { rangeAddress: "C6", value: "Quantity", name: "\\quantity", Halignment: "Center" },
        { rangeAddress: "D6", value: "UOM", name: "\\uom", Halignment: "Center" },
        { rangeAddress: "E6", value: "Unit Cost", name: "\\total_uc", Halignment: "Center" },
        { rangeAddress: "F6", value: "Total", name: "\\total", Halignment: "Right" },
      ];
      lineItemHeaders.forEach((header) => {
        header.fontSize = 12;
        header.bold = true;
      });
      applyRangeFormats(sheet, lineItemHeaders);
      applyGridStyle({
        range: sheet.getRange("B6:F6"),
        borderColor: "Black",
        weight: "Medium",
        edges: ["EdgeBottom"],
      });

      const lineItemRows: rangeFormatting[] = [
        { rangeAddress: "B7", name: rowNames.gcgrLineItems },
        { rangeAddress: "C7", numberFormat: QUANTITY_NUMBER_FORMAT, Halignment: "Right" },
        { rangeAddress: "D7", Halignment: "Left" },
        { rangeAddress: "E7", numberFormat: UNITCOST_NUMBER_FORMAT, Halignment: "Right" },
        { rangeAddress: "F7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
      ];
      applyRangeFormats(sheet, lineItemRows);
      await context.sync();
      await generateGCGRLineReport(sheet);
      sheet.activate();
      await context.sync();
    });
  } catch (error) {
    throw "Error creating default GCGR template: " + error;
  }
};
